import { useState } from "react";
import KpiTile from "@/components/KpiTile";
import DateRangePicker from "@/components/DateRangePicker";
import { useKpis } from "@/lib/hooks";
import { presets, type Range } from "@/lib/dates";
import { fmtUSDCompact, fmtInt } from "@/lib/format";

const defaultPreset = presets[0];

export default function Home() {
  const [range, setRange] = useState<Range>(defaultPreset.range());
  const [presetId, setPresetId] = useState<string | null>(defaultPreset.id);

  const { data, isLoading, error } = useKpis(range.from, range.to);

  const revenueDelta = data && data.revenue_prior > 0
    ? ((data.revenue_current - data.revenue_prior) / data.revenue_prior) * 100
    : null;
  const qtyDelta = data && data.qty_prior > 0
    ? ((data.qty_current - data.qty_prior) / data.qty_prior) * 100
    : null;
  const activeDelta = data && data.active_customers_prior > 0
    ? ((data.active_customers_current - data.active_customers_prior) / data.active_customers_prior) * 100
    : null;

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Overview</h1>
          <p className="mt-1 text-sm text-ink-500">
            Performance vs same window prior year.
          </p>
        </div>
        <DateRangePicker
          value={range}
          presetId={presetId}
          onChange={(r, id) => {
            setRange(r);
            setPresetId(id);
          }}
        />
      </header>

      {error && (
        <div className="card mb-6 p-4 text-sm text-danger">
          Failed to load KPIs: {(error as Error).message}
        </div>
      )}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile
          label="Revenue"
          value={fmtUSDCompact(data?.revenue_current)}
          deltaPct={revenueDelta}
          hint="vs prior year"
          loading={isLoading}
        />
        <KpiTile
          label="Quantity"
          value={fmtInt(data?.qty_current)}
          deltaPct={qtyDelta}
          hint="vs prior year"
          loading={isLoading}
        />
        <KpiTile
          label="Active customers"
          value={fmtInt(data?.active_customers_current)}
          deltaPct={activeDelta}
          hint="ordered in window"
          loading={isLoading}
        />
        <KpiTile
          label="At-risk customers"
          value={fmtInt(data?.at_risk_customers)}
          hint="declining >25% YoY"
          loading={isLoading}
          to="/segmentation?focus=at-risk"
        />
      </section>

      {data && (
        <p className="mt-6 text-xs text-ink-500">
          Window: {data.period.from} → {data.period.to}. Compared to {data.prior_period.from} → {data.prior_period.to}.
        </p>
      )}
    </div>
  );
}
